import { useEffect, useRef, useState } from "react";
import {
  createAndAddUserToGroup,
  getGroupMembersInfo,
  getUsersNotInGroup,
  sendTIMReport,
  updateGroup,
  uploadCsv,
} from "../axios/axiosFunctions";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { columnsGroups } from "../utils/groupsTable";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PersonIcon from "@mui/icons-material/Person";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import TeamCard from "./TeamCard";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const defaultUser = {
  firstName: "",
  lastName: "",
  email: "",
  type: "",
};

const TeamsCards = ({ group, callGroups, onEdit, onDelete }) => {
  const fileInputRef = useRef(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [rows, setRows] = useState([{ id: 1 }]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [selectOpt, setSelectOpt] = useState([]);
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [msg, setMsg] = useState("");
  const [showMsg, setShowMsg] = useState(false);
  const [msgSeverity, setMsgSeverity] = useState("success");
  const [msg2, setMsg2] = useState("");
  const [showMsg2, setShowMsg2] = useState(false);
  const [msgSeverity2, setMsgSeverity2] = useState("success");
  const [loading, setLoading] = useState(true);
  const [openDialogCreate, setOpenDialogCreate] = useState(false);
  const [openConfirmSend, setOpenConfirmSend] = useState(false);
  const [reportSent, setReportSent] = useState(group?.completed || false);
  const [sendingReport, setSendingReport] = useState(false);
  const [userForm, setUserForm] = useState(defaultUser);
  const { firstName, lastName, email, type } = userForm;

  useEffect(() => {
    updateTable();
  }, [group]);

  const updateTable = () => {
    setLoading(true);
    callMembersInfo();
    callUsersToAdd();
  };

  const callMembersInfo = async () => {
    const info = await getGroupMembersInfo(group._id);
    const tempRows = info.data.map((u) => ({
      id: u._id,
      firstName: u.firstName || "",
      lastName: u.lastName || "",
      email: u.email,
      type: u.rol,
      status: u.status,
      userId: u._id,
      groupId: group._id,
    }));
    setRows(tempRows);
    setLoading(false);
  };

  const callUsersToAdd = async () => {
    const res = await getUsersNotInGroup(group._id);
    if (res) {
      setUsers(res.users);
      mapOptions(res.users);
    }
  };

  const mapOptions = (usersArray) => {
    if (!usersArray || usersArray.length === 0) {
      setSelectOpt([]);
      return;
    }

    const tempOpt = usersArray.map((u, i) => ({
      label: `${u.firstName}${u.lastName ? ` ${u.lastName}` : ""}`,
      index: i,
    }));
    setSelectOpt(tempOpt);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleUploadFile = async (e) => {
    const { length } = e.target.files;
    if (length !== 0) {
      const formData = new FormData();
      formData.append("file", e.target.files[0]);
      formData.append("groupId", group._id);

      const resp = await uploadCsv(formData);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (resp) {
        handleDialogClose();
        callGroups();
      }
    }
  };

  const showMemberSelect = () => {
    setShowDropdown(!showDropdown);
  };

  const handleChange = (e, newValue) => {
    if (!newValue) return;
    const { index } = newValue;
    const newUsersList = [...usersList];
    const selectedElemet = users[index];

    const newUsersToAdd = [...users.slice(0, index), ...users.slice(index + 1)];

    newUsersList.push(selectedElemet);
    setUsers(newUsersToAdd);
    mapOptions(newUsersToAdd);
    setUsersList(newUsersList);
  };

  const submitNewMembers = async () => {
    setLoading(true);
    if (usersList.length === 0) {
      return;
    }

    const usersToAdd = usersList.map((u) => u._id);
    const currentUsers = rows.map((u) => u.userId);

    const data = {
      members: usersToAdd.concat(currentUsers),
    };

    const res = await updateGroup(group._id, data);
    if (!res) {
      setError("Error while saving please try later");
      setShowError(true);
      setAlertSeverity("error");
      setTimeout(() => {
        setShowError(false);
      }, 4000);
      return;
    }
    callMembersInfo();
    callUsersToAdd();
    cancelAddNewMembers();
    callGroups();
  };

  const cancelAddNewMembers = () => {
    if (usersList.length === 0) {
      showMemberSelect();
      return;
    }
    setUsersList([]);
    callUsersToAdd();
    showMemberSelect();
  };

  const handleChangeCreate = (e) => {
    const { name, value } = e.target;
    setUserForm({ ...userForm, [name]: value });
  };

  const handlePreviewReport = () => {
    window.open(`/tim_self_assessment/${group._id}/report`, '_blank');
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    setOpenConfirmSend(false);
    const frontUrl = process.env.REACT_APP_FRONT_URL || "https://assessments.know2grow.com/";
    const res = await sendTIMReport(group._id, { frontUrl });
    setSendingReport(false);
    if (res.error) {
      setMsg("Error while sending report, please try later");
      setShowMsg(true);
      setMsgSeverity("error");
      setTimeout(() => { setShowMsg(false); }, 4000);
      return;
    }
    setReportSent(true);
    setMsg(`Report sent! ${res.emailsSent} email(s) delivered.`);
    setShowMsg(true);
    setMsgSeverity("success");
    setTimeout(() => { setShowMsg(false); }, 4000);
  };

  const validateCreateUser = () => {
    return (
      firstName !== "" &&
      lastName !== "" &&
      email !== "" &&
      type !== ""
    );
  };

  const handleCloseDialogCreate = () => {
    setOpenDialogCreate(false);
  };

  const handleCreateUser = async () => {
    const isValid = validateCreateUser();
    if (!isValid) {
      setMsg2("Complete all fields");
      setShowMsg2(true);
      setMsgSeverity2("error");
      setTimeout(() => {
        setShowMsg2(false);
      }, 2000);
      return;
    }

    const res = await createAndAddUserToGroup(group._id, userForm);
    if (!res.user) {
      setMsg2(res.msg);
      setShowMsg2(true);
      setMsgSeverity2("error");
      setTimeout(() => {
        setShowMsg2(false);
      }, 2000);
      return;
    }
    setMsg2("User created correctly");
    setShowMsg2(true);
    setMsgSeverity2("success");
    setTimeout(() => {
      setShowMsg2(false);
      updateTable();
      callGroups();
      setUserForm(defaultUser);
      handleCloseDialogCreate();
    }, 2000);
    return;
  };

  return (
    <>
      <TeamCard group={group} loading={loading} onClick={handleOpenDialog} onEdit={onEdit} onDelete={onDelete} />
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h5" sx={{ textAlign: "center" }}>
            {group.name}
          </Typography>
          <Divider sx={{ margin: "10px 0px" }} />
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            MEMBERS
          </Typography>
          <DataGrid
            columns={columnsGroups}
            rows={rows.map((row) => ({ ...row, updateTable, callGroups }))}
          />
          {showMsg && (
            <Alert sx={{ marginTop: "10px" }} severity={msgSeverity}>
              {msg}
            </Alert>
          )}
          <Box
            sx={{
              marginTop: "20px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button onClick={handlePreviewReport} variant="outlined">
              PREVIEW REPORT
            </Button>
            {reportSent ? (
              <Button disabled variant="contained" color="success">
                REPORT SENT ✓
              </Button>
            ) : (
              <Button
                onClick={() => setOpenConfirmSend(true)}
                variant="contained"
                color="primary"
                disabled={sendingReport}
              >
                {sendingReport ? <CircularProgress size={24} color="inherit" /> : "SEND REPORT"}
              </Button>
            )}
          </Box>
          <Typography
            variant="h6"
            sx={{ textAlign: "center", marginTop: "20px" }}
          >
            ADD MEMBERS
          </Typography>
          <Divider sx={{ margin: "10px 0px" }} />
          <Box display={"flex"} justifyContent={"space-evenly"}>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              UPLOAD FILE
              <VisuallyHiddenInput
                onChange={handleUploadFile}
                ref={fileInputRef}
                type="file"
              />
            </Button>
            {!showDropdown && (
              <Button onClick={showMemberSelect} variant="contained">
                ADD MEMBER
              </Button>
            )}
          </Box>
          {showDropdown && (
            <>
              <Button
                onClick={() => setOpenDialogCreate(true)}
                startIcon={<AddCircleOutlineIcon />}
                variant="contained"
              >
                Create User
              </Button>
              <Autocomplete
                onChange={handleChange}
                options={selectOpt}
                renderInput={(params) => (
                  <TextField {...params} label="Search existing user" />
                )}
                sx={{ margin: "20px 0px" }}
              />
              {usersList.length === 0 && (
                <Box display={"flex"} justifyContent={"space-evenly"}>
                  <Button onClick={cancelAddNewMembers} variant="contained">
                    CANCEL
                  </Button>
                </Box>
              )}
              <Dialog open={openDialogCreate} onClose={handleCloseDialogCreate}>
                <DialogContent>
                  <Typography variant="h5" color="primary">
                    CREATE USER
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="subtitle1">First Name</Typography>
                      <TextField
                        onChange={handleChangeCreate}
                        value={firstName}
                        name="firstName"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="subtitle1">Last Name</Typography>
                      <TextField
                        onChange={handleChangeCreate}
                        value={lastName}
                        name="lastName"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="subtitle1">Email</Typography>
                      <TextField
                        onChange={handleChangeCreate}
                        value={email}
                        name="email"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="subtitle1">Type</Typography>
                      <FormControl sx={{ width: "100%" }}>
                        <Select
                          onChange={handleChangeCreate}
                          value={type}
                          name="type"
                          fullWidth
                        >
                          <MenuItem value={"member"}>Member</MenuItem>
                          <MenuItem value={"senior"}>Senior</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {showMsg2 && (
                      <Grid size={12}>
                        <Alert
                          sx={{ marginTop: "20px" }}
                          severity={msgSeverity2}
                        >
                          {msg2}
                        </Alert>
                      </Grid>
                    )}
                    <Grid
                      size={12}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      <Button onClick={handleCreateUser} variant="contained">
                        CREATE
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleCloseDialogCreate}
                        sx={{ marginLeft: "20px" }}
                      >
                        CANCEL
                      </Button>
                    </Grid>
                  </Grid>
                </DialogContent>
              </Dialog>
            </>
          )}
          {usersList.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                fontWeight={500}
                color="secondary"
                sx={{ marginTop: "20px" }}
              >
                Users to Add
              </Typography>
              <List>
                {usersList.map((u, i) => (
                  <ListItem key={i}>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${u.firstName}${u.lastName ? ` ${u.lastName}` : ""}`}
                    />
                  </ListItem>
                ))}
              </List>
              {showError && (
                <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>
                  {error}
                </Alert>
              )}
              <Box display={"flex"} justifyContent={"space-evenly"}>
                <Button onClick={submitNewMembers} variant="contained">
                  ADD
                </Button>
                <Button onClick={cancelAddNewMembers} variant="contained">
                  CANCEL
                </Button>
              </Box>
            </>
          )}
          <Box
            sx={{ display: "flex", margin: "20px", justifyContent: "center" }}
          >
            <Button
              variant="contained"
              onClick={handleDialogClose}
              color="customGray"
            >
              CLOSE
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Send Report Confirmation Dialog */}
      <Dialog open={openConfirmSend} onClose={() => setOpenConfirmSend(false)}>
        <DialogTitle>Send Report to Participants</DialogTitle>
        <DialogContent>
          <Typography>
            This will release the report to all group members and send them an email notification. This action cannot be undone.
          </Typography>
          <Typography sx={{ marginTop: "10px", fontWeight: 600 }}>
            Are you sure you want to send the report?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmSend(false)} color="inherit">
            CANCEL
          </Button>
          <Button onClick={handleSendReport} variant="contained" color="primary">
            YES, SEND REPORT
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeamsCards;
